import CurrentsApiService from './CurrentsApiService';
import NewsDataService from './NewsDataService';
import MockDataService from './MockDataService';

class NewsService {
  constructor() {
    this.newsApiKey = null;
    this.currentsApiKey = null;
    this.newsDataApiKey = null;
    this.baseUrl = 'https://newsapi.org/v2';
    this.proxyUrl = '/api/news';
    this.serverUrl = 'http://localhost:3001';
    this.useProxy = true;
    this.serverAvailable = false;
    this.useMockData = false;
  }

  async init(newsApiKey, currentsApiKey = null, useMockData = false, newsDataApiKey = null) {
    this.newsApiKey = newsApiKey;
    this.useMockData = useMockData;
    
    if (currentsApiKey) {
      this.currentsApiKey = currentsApiKey;
      CurrentsApiService.init(currentsApiKey);
    }
    
    if (newsDataApiKey) {
      this.newsDataApiKey = newsDataApiKey;
      NewsDataService.init(newsDataApiKey);
    }
    
    // Проверяем доступность сервера
    await this.checkServerAvailability();
  }

  async checkServerAvailability() {
    try {
      console.log('Проверка доступности сервера...');
      // Проверяем несколько возможных URL сервера
      const urls = [
        '/api/test',
        'http://localhost:3001/api/test',
        'http://127.0.0.1:3001/api/test'
      ];
      
      let serverFound = false;
      
      for (const url of urls) {
        try {
          console.log(`Проверка URL: ${url}`);
          const response = await fetch(url, { 
            method: 'GET',
            headers: { 'Content-Type': 'application/json' },
            mode: 'cors'
          });
          
          if (response.ok) {
            const data = await response.json();
            console.log('Сервер доступен:', data.message);
            this.serverAvailable = true;
            serverFound = true;
            
            // Если сервер найден по относительному URL, используем его
            if (url.startsWith('/')) {
              this.serverUrl = '';
            } else {
              // Иначе используем полный URL
              this.serverUrl = url.substring(0, url.indexOf('/api'));
            }
            
            console.log(`Используем URL сервера: ${this.serverUrl}`);
            break;
          }
        } catch (e) {
          console.log(`URL ${url} недоступен:`, e.message);
        }
      }
      
      if (!serverFound) {
        console.warn('Сервер недоступен, будут использованы прямые запросы к API');
        this.serverAvailable = false;
      }
    } catch (error) {
      console.error('Ошибка при проверке доступности сервера:', error);
      this.serverAvailable = false;
    }
  }

  async getNews(keywords, language, hoursAgo, options = {}) {
    try {
      console.log(`Получение новостей для ключевых слов: ${keywords.join(', ')}`);
      
      // Если включен режим тестовых данных, возвращаем их
      if (this.useMockData) {
        console.log('Используем тестовые данные');
        return await MockDataService.getNews(keywords, language, hoursAgo);
      }
      
      // Настройки по умолчанию
      const settings = {
        useNewsApi: true,
        useCurrentsApi: true,
        useNewsDataApi: true,
        ...options
      };
      
      // Массив для хранения результатов из всех источников
      let allResults = [];
      let hasResults = false;
      
      // Получаем новости из NewsData.io API, если он выбран и ключ доступен
      if (settings.useNewsDataApi && this.newsDataApiKey) {
        try {
          console.log('Запрос к NewsData.io API через прокси...');
          const newsDataResults = await NewsDataService.getNews(keywords, language, hoursAgo);
          if (newsDataResults.length > 0) {
            allResults = [...allResults, ...newsDataResults];
            hasResults = true;
            console.log(`Получено ${newsDataResults.length} новостей из NewsData.io API`);
          }
        } catch (error) {
          console.error('Ошибка при получении новостей из NewsData.io API:', error);
        }
      }
      
      // Если сервер доступен и выбраны API, пытаемся получить данные через прокси
      if (this.serverAvailable) {
        // Получаем новости из News API, если он выбран и ключ доступен
        if (settings.useNewsApi && this.newsApiKey) {
          try {
            console.log('Запрос к News API через прокси...');
            const newsApiResults = await this.getNewsFromNewsApi(keywords, language, hoursAgo);
            if (newsApiResults.length > 0) {
              allResults = [...allResults, ...newsApiResults];
              hasResults = true;
              console.log(`Получено ${newsApiResults.length} новостей из News API`);
            }
          } catch (error) {
            console.error('Ошибка при получении новостей из News API:', error);
          }
        }
        
        // Получаем новости из Currents API, если он выбран и ключ доступен
        if (settings.useCurrentsApi && this.currentsApiKey) {
          try {
            console.log('Запрос к Currents API через прокси...');
            const currentsApiResults = await CurrentsApiService.getNews(keywords, language, hoursAgo);
            if (currentsApiResults.length > 0) {
              allResults = [...allResults, ...currentsApiResults];
              hasResults = true;
              console.log(`Получено ${currentsApiResults.length} новостей из Currents API`);
            }
          } catch (error) {
            console.error('Ошибка при получении новостей из Currents API:', error);
          }
        }
      } else {
        console.warn('Сервер недоступен, попытка прямого запроса к API...');
        
        // Пытаемся сделать прямой запрос к API (может не работать из-за CORS)
        try {
          if (settings.useNewsApi && this.newsApiKey) {
            console.log('Прямой запрос к News API...');
            const directResults = await this.getNewsDirectFromNewsApi(keywords, language, hoursAgo);
            if (directResults.length > 0) {
              allResults = [...allResults, ...directResults];
              hasResults = true;
              console.log(`Получено ${directResults.length} новостей из прямого запроса к News API`);
            }
          }
        } catch (error) {
          console.error('Ошибка при прямом запросе к News API:', error);
        }
        
        // Пытаемся сделать прямой запрос к Currents API
        try {
          if (settings.useCurrentsApi && this.currentsApiKey) {
            console.log('Прямой запрос к Currents API...');
            const currentsDirectResults = await CurrentsApiService.getNewsDirect(keywords, language, hoursAgo);
            if (currentsDirectResults.length > 0) {
              allResults = [...allResults, ...currentsDirectResults];
              hasResults = true;
              console.log(`Получено ${currentsDirectResults.length} новостей из прямого запроса к Currents API`);
            }
          }
        } catch (error) {
          console.error('Ошибка при прямом запросе к Currents API:', error);
        }
      }
      
      // Если все равно нет результатов, возвращаем пустой массив
      if (allResults.length === 0) {
        console.warn('Не удалось получить новости из всех источников');
        return [];
      }
      
      // Удаляем дубликаты по URL
      const uniqueResults = this.removeDuplicates(allResults);
      
      // Сортируем по дате публикации (от новых к старым)
      uniqueResults.sort((a, b) => new Date(b.publishedAt) - new Date(a.publishedAt));
      
      console.log(`Всего получено ${uniqueResults.length} уникальных новостей`);
      return uniqueResults;
    } catch (error) {
      console.error('Критическая ошибка при получении новостей:', error);
      return [];
    }
  }
  
  async getNewsFromNewsApi(keywords, language, hoursAgo) {
    if (!this.newsApiKey) {
      throw new Error('News API Key не установлен');
    }

    try {
      // Формируем дату для поиска (текущая дата минус указанное количество часов)
      const now = new Date();
      const fromDate = new Date(now.getTime() - hoursAgo * 60 * 60 * 1000);
      
      // Форматируем дату в формат ISO
      const fromDateStr = fromDate.toISOString();
      
      // Формируем запрос
      const query = keywords.join(' OR ');
      
      // Параметры запроса
      const params = {
        q: query,
        language: language,
        from: fromDateStr,
        sortBy: 'publishedAt',
        apiKey: this.newsApiKey
      };
      
      console.log('Отправка запроса к News API через прокси');
      
      // Полный URL для запроса
      const url = `${this.serverUrl}${this.proxyUrl}`;
      console.log(`Используем URL: ${url}`);
      
      // Выполняем запрос через прокси
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(params)
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Ошибка API: ${response.status} ${response.statusText} - ${errorText}`);
      }
      
      const data = await response.json();
      
      if (!data.articles || !Array.isArray(data.articles)) {
        console.warn('News API вернул неожиданный формат данных:', data);
        return [];
      }
      
      // Преобразуем данные в нужный формат
      const formattedResults = data.articles.map(article => ({
        title: article.title,
        description: article.description || article.content || '',
        url: article.url,
        image: article.urlToImage,
        publishedAt: article.publishedAt,
        source: article.source?.name || 'News API',
        apiSource: 'newsapi'
      }));
      
      return formattedResults;
    } catch (error) {
      console.error('Ошибка при получении новостей из News API:', error);
      return [];
    }
  }
  
  // Прямой запрос к News API (может не работать из-за CORS)
  async getNewsDirectFromNewsApi(keywords, language, hoursAgo) {
    if (!this.newsApiKey) {
      throw new Error('News API Key не установлен');
    }
    
    try {
      // Формируем дату для поиска
      const now = new Date();
      const fromDate = new Date(now.getTime() - hoursAgo * 60 * 60 * 1000);
      const fromDateStr = fromDate.toISOString();
      
      // Формируем запрос
      const query = keywords.join(' OR ');
      
      // Параметры запроса
      const params = new URLSearchParams({
        q: query,
        language: language,
        from: fromDateStr,
        sortBy: 'publishedAt',
        apiKey: this.newsApiKey
      });
      
      const url = `${this.baseUrl}/everything?${params.toString()}`;
      console.log('Прямой запрос к News API:', url);
      
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error(`Ошибка API: ${response.status} ${response.statusText}`);
      }
      
      const data = await response.json();
      
      if (!data.articles || !Array.isArray(data.articles)) {
        return [];
      }
      
      // Преобразуем данные в нужный формат
      const formattedResults = data.articles.map(article => ({
        title: article.title,
        description: article.description || article.content || '',
        url: article.url,
        image: article.urlToImage,
        publishedAt: article.publishedAt,
        source: article.source?.name || 'News API',
        apiSource: 'newsapi'
      }));
      
      return formattedResults;
    } catch (error) {
      console.error('Ошибка при прямом запросе к News API:', error);
      return [];
    }
  }
  
  // Удаление дубликатов по URL
  removeDuplicates(articles) {
    const uniqueUrls = new Set();
    return articles.filter(article => {
      if (!article.url || uniqueUrls.has(article.url)) {
        return false;
      }
      uniqueUrls.add(article.url);
      return true;
    });
  }
}

export default new NewsService();